import { type FormEvent, useState } from "react"
import { api } from "../lib/api"
import { normaliseShopDomain } from "../lib/shopify"
import { Button, Card, ErrorNote, Field, Select, Spinner, useLoad } from "./ui"
import { ShopifyLogo } from "./logos"

// The Shopify tile. Unlike the requirements-driven tiles, a store connection
// has exactly one door: the Connect button's OAuth redirect. There is no form
// for the token, and nothing here can write one — the server refuses it too.
//
// The list comes from /shopify/connections rather than /connections because it
// also carries `enabled`: the company kill switch. A disabled company sees the
// note and no form.

type ShopifyConnection = {
  id: string
  label: string
  shopDomain: string
  accessPolicy: string
  scopes: string[]
  active: boolean
  status: "connected" | "needs_reconnect"
  revision: number
  createdAt: string
}

const POLICIES = [
  { value: "READ_ONLY", label: "Read only" },
  { value: "WRITES_WITH_APPROVAL", label: "Writes with approval" },
  { value: "BLOCKED", label: "Blocked" },
]

export function ShopifyConnectionsTile() {
  const list = useLoad(() => api.get<{ enabled: boolean; connections: ShopifyConnection[] }>("/shopify/connections"))
  const [shop, setShop] = useState("")
  const [busy, setBusy] = useState<string | null>(null)
  const [error, setError] = useState<unknown>(null)
  const enabled = list.data?.enabled === true
  const domain = normaliseShopDomain(shop)

  // The full-page redirect: the URL comes from the server and the browser
  // leaves. The callback brings it back with ?shopify=…, which the page's
  // banner reads.
  async function connect(target: string) {
    setBusy("connect")
    setError(null)
    try {
      const { url } = await api.get<{ url: string }>(`/shopify/authorize-url?shop=${encodeURIComponent(target)}`)
      window.location.href = url
    } catch (err) {
      setError(err)
      setBusy(null)
    }
  }

  async function setPolicy(connection: ShopifyConnection, accessPolicy: string) {
    setBusy(connection.id)
    setError(null)
    try {
      await api.patch(`/connections/${connection.id}`, { accessPolicy })
      list.reload()
    } catch (err) {
      setError(err)
    } finally {
      setBusy(null)
    }
  }

  async function remove(connection: ShopifyConnection) {
    if (!confirm(`Disconnect ${connection.shopDomain}? Your agent sessions lose access to this store.`)) return
    setBusy(connection.id)
    setError(null)
    try {
      await api.delete(`/connections/${connection.id}`)
      list.reload()
    } catch (err) {
      setError(err)
    } finally {
      setBusy(null)
    }
  }

  return (
    <Card className="flex flex-col items-start gap-3 p-5">
      <div className="flex h-16 w-16 items-center justify-center rounded-xl border border-border bg-background p-3 shadow-sm">
        <ShopifyLogo className="h-full w-full" />
      </div>
      <div>
        <p className="font-medium text-foreground">Shopify</p>
        <p className="text-xs text-muted">Connected stores — products, inventory and content through the agent's Shopify tools.</p>
        <p className="mt-1 text-xs text-muted">Personal connection · the token stays encrypted in Moderniser and never enters a session container.</p>
      </div>

      <ErrorNote error={list.error ?? error} />
      {list.loading && !list.data ? <Spinner label="Loading your stores" /> : null}
      {list.data && !enabled && <p className="text-xs text-warning">This connector has been disabled for your company.</p>}

      {enabled && (
        <form
          className="flex w-full flex-col gap-2"
          onSubmit={(event: FormEvent) => {
            event.preventDefault()
            if (domain) void connect(domain)
          }}
        >
          <Field
            label="Store domain"
            name="shopifyShop"
            placeholder="your-store.myshopify.com"
            value={shop}
            onChange={(event) => setShop(event.target.value)}
            autoComplete="off"
          />
          <Button type="submit" disabled={!domain || busy !== null}>
            {busy === "connect" ? "Redirecting…" : "Connect"}
          </Button>
          <p className="text-xs text-muted">You approve the access in the store's admin. The token is stored encrypted in Moderniser.</p>
        </form>
      )}

      {list.data?.connections.map((connection) => (
        <div key={connection.id} className="w-full rounded-md border border-border p-3">
          <p className="text-sm font-medium text-foreground">{connection.label}</p>
          <p className="text-xs text-muted">
            {connection.shopDomain}
            {connection.status === "needs_reconnect" ? <span className="text-warning"> · needs reconnect</span> : ""}
          </p>
          <div className="mt-2">
            <Select
              label="Access policy"
              name={`policy-${connection.id}`}
              value={connection.accessPolicy}
              disabled={busy !== null}
              onChange={(event) => void setPolicy(connection, event.target.value)}
            >
              {POLICIES.map((policy) => (
                <option key={policy.value} value={policy.value}>{policy.label}</option>
              ))}
            </Select>
          </div>
          <div className="mt-2 flex flex-wrap gap-2">
            <Button size="sm" variant="ghost" disabled={busy !== null} onClick={() => void connect(connection.shopDomain)}>
              Reconnect
            </Button>
            <Button size="sm" variant="danger" disabled={busy !== null} onClick={() => void remove(connection)}>
              Disconnect
            </Button>
          </div>
        </div>
      ))}
    </Card>
  )
}
